import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  ChevronRight,
  Clock3,
  MapPin,
  PhoneCall,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { STATUS_BEGIN, STATUS_DONE, STATUS_FAIL, ENTITY_TYPE_SELLER_OUTLET } from "utils/constants/others";
import usePlanShipmentStore from "./usePlanShipmentStore";
import {
  filterShippingNotes,
  formatShippingNoteSlot,
  formatShippingNoteStatus,
  getCourierDocumentNumber,
  getShippingNoteWarehouseCode,
  SHIPPING_NOTE_FILTERS,
} from "./planShipmentShippingNotesUtils";
import {
  formatScheduleDate,
  formatShippingNoteNumber,
  formatWeight,
  formatWeightValue,
} from "./planShipmentUtils";
import {
  AllCaughtUpIcon,
  AllCaughtUpState,
  AllCaughtUpTitle,
  EmptyState,
  ErrorMessage,
  RetryButton,
  SkeletonCard,
  SkeletonLine,
  StateTitle,
} from "./styles.planShipment";
import {
  CallButton,
  DeliveryCopy,
  DeliveryDetails,
  ShippingNoteArrow,
  ShippingNoteCard,
  ShippingNoteCards,
  ShippingNoteHeader,
  ShippingNoteMain,
  ShippingNoteMetadata,
  ShippingNoteMetaItem,
  ShippingNoteStatus,
  ShippingNoteSubtitle,
  ShippingNoteTab,
  ShippingNoteTabs,
  ShippingNoteTitle,
} from "./styles.shippingNotesList";

const PAGE_SIZE = 20;

const formatCount = (value) => formatWeightValue(value) || "0";

const ShippingNoteListSkeleton = () => (
  <ShippingNoteCards aria-label="Loading shipping notes">
    {[0, 1, 2].map((item) => (
      <SkeletonCard key={item}>
        <SkeletonLine $width="104px" />
        <SkeletonLine $width="210px" $height="22px" />
        <SkeletonLine $width="180px" />
        <SkeletonLine $height="82px" />
        <SkeletonLine $height="54px" />
      </SkeletonCard>
    ))}
  </ShippingNoteCards>
);

export const ShippingNoteDeliveryDetails = ({ shippingNote }) => {
  const deliveryDetails = shippingNote?.delivery_details || {};
  const deliveryType = String(
    deliveryDetails.delivery_type || ""
  ).toLowerCase();

  if (deliveryType === "courier") {
    return (
      <DeliveryDetails>
        <DeliveryCopy>
          <strong>{deliveryDetails.da_name || "—"}</strong>
          <span>{getCourierDocumentNumber(shippingNote)}</span>
        </DeliveryCopy>
      </DeliveryDetails>
    );
  }

  const phoneNumber = String(deliveryDetails.da_identifier_value || "").trim();
  const callableNumber = phoneNumber.replace(/[^+\d]/g, "");

  return (
    <DeliveryDetails>
      <DeliveryCopy>
        <strong>{deliveryDetails.vehicle_number || "—"}</strong>
        <span>{deliveryDetails.da_name || "—"}</span>
      </DeliveryCopy>
      {callableNumber ? (
        <CallButton
          href={`tel:${callableNumber}`}
          aria-label={`Call ${deliveryDetails.da_name || "driver"}`}
        >
          <PhoneCall aria-hidden="true" />
        </CallButton>
      ) : null}
    </DeliveryDetails>
  );
};

export const ShippingNoteListCard = ({ shippingNote, onOpen }) => {
  const invoiceCount = Number(shippingNote?.invoice_count) || 0;

  return (
    <ShippingNoteCard onClick={onOpen} style={{ cursor: "pointer" }}>
      <ShippingNoteStatus $status={shippingNote.status}>
        {formatShippingNoteStatus(shippingNote.status)}
      </ShippingNoteStatus>
      <ShippingNoteMain>
        <ShippingNoteHeader>
          <div>
            <ShippingNoteTitle>
              {formatShippingNoteNumber(shippingNote)}
            </ShippingNoteTitle>
            <ShippingNoteSubtitle>
              {invoiceCount} {invoiceCount === 1 ? "Invoice" : "Invoices"}
              {" • "}
              {formatWeight(shippingNote.total_weight_in_kg)}
            </ShippingNoteSubtitle>
          </div>
          <ShippingNoteArrow
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            aria-label={`Open ${formatShippingNoteNumber(shippingNote)}`}
          >
            <ChevronRight size={27} strokeWidth={1.8} />
          </ShippingNoteArrow>
        </ShippingNoteHeader>

        <ShippingNoteMetadata>
          <ShippingNoteMetaItem>
            <CalendarDays aria-hidden="true" />
            <span>{formatScheduleDate(shippingNote.delivery_date)}</span>
          </ShippingNoteMetaItem>
          <ShippingNoteMetaItem>
            <Clock3 aria-hidden="true" />
            <span>{formatShippingNoteSlot(shippingNote.slot)}</span>
          </ShippingNoteMetaItem>
          <ShippingNoteMetaItem>
            <MapPin aria-hidden="true" />
            <span>
              {(() => {
                const raw = shippingNote?.warehouse?.warehouse_name || shippingNote?.warehouse?.warehouse_code || "";
                if (!raw) return "—";
                const commaIndex = raw.indexOf(",");
                if (commaIndex === -1) return raw;
                const code = raw.slice(0, commaIndex).trim();
                const city = raw.slice(commaIndex + 1).trim();
                return (
                  <>
                    {code}
                    {city ? <><br />{city}</> : null}
                  </>
                );
              })()}
            </span>
          </ShippingNoteMetaItem>
        </ShippingNoteMetadata>
      </ShippingNoteMain>
      <ShippingNoteDeliveryDetails shippingNote={shippingNote} />
    </ShippingNoteCard>
  );
};

const ShippingNotesList = ({ searchQuery = "" }) => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("dispatched");
  const {
    getAdvanceShippingNotes,
    getAdvanceShippingNotesStatus,
    advanceShippingNotes,
    advanceShippingNotesHasMore,
    loadMoreAdvanceShippingNotes,
    advanceShippingNotesError,
    searchAdvanceShippingNotes,
  } = usePlanShipmentStore();
  const sentinelRef = useRef(null);
  const vendorId = window.localStorage.getItem("outlet_id");
  const hasValidVendorId =
    Number.isInteger(Number(vendorId)) && Number(vendorId) > 0;
  const trimmedSearchQuery = searchQuery.trim();

  const activeFilterStatuses = useMemo(
    () =>
      (SHIPPING_NOTE_FILTERS.find((f) => f.id === activeFilter) || SHIPPING_NOTE_FILTERS[0])
        .statuses,
    [activeFilter]
  );

  const fetchShippingNotes = useCallback(() => {
    if (!hasValidVendorId) return;

    getAdvanceShippingNotes({
      entity_type: ENTITY_TYPE_SELLER_OUTLET,
      entity_id: vendorId,
      page_size: PAGE_SIZE,
      page_number: 1,
      statuses: activeFilterStatuses,
    });
  }, [getAdvanceShippingNotes, hasValidVendorId, vendorId, activeFilterStatuses]);

  useEffect(() => {
    if (!hasValidVendorId) return;

    if (!trimmedSearchQuery) {
      fetchShippingNotes();
      return;
    }

    const timeoutId = setTimeout(() => {
      searchAdvanceShippingNotes({
        entity_type: ENTITY_TYPE_SELLER_OUTLET,
        entity_id: vendorId,
        asn_id: Number(trimmedSearchQuery),
      });
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [
    hasValidVendorId,
    trimmedSearchQuery,
    fetchShippingNotes,
    searchAdvanceShippingNotes,
    vendorId,
  ]);

  // When a search returns a result, switch to the tab matching its status.
  useEffect(() => {
    if (!trimmedSearchQuery || getAdvanceShippingNotesStatus !== STATUS_DONE) {
      return;
    }

    const foundStatus = String(
      advanceShippingNotes[0]?.status || ""
    ).toUpperCase();
    if (!foundStatus) return;

    const matchingFilter = SHIPPING_NOTE_FILTERS.find(
      (filter) => filter.id !== "all" && filter.statuses.includes(foundStatus)
    );
    if (matchingFilter && matchingFilter.id !== activeFilter) {
      setActiveFilter(matchingFilter.id);
    }
  }, [trimmedSearchQuery, getAdvanceShippingNotesStatus, advanceShippingNotes]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreAdvanceShippingNotes(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMoreAdvanceShippingNotes, advanceShippingNotesHasMore, advanceShippingNotes.length]);

  const visibleShippingNotes = useMemo(() => {
    if (trimmedSearchQuery) return advanceShippingNotes;
    return filterShippingNotes(advanceShippingNotes, activeFilter, "");
  }, [activeFilter, advanceShippingNotes, trimmedSearchQuery]);
  const isLoading =
    (getAdvanceShippingNotesStatus === STATUS_BEGIN ||
      !getAdvanceShippingNotesStatus) &&
    advanceShippingNotes.length === 0;

  return (
    <>
      <ShippingNoteTabs aria-label="Shipping note status">
        {SHIPPING_NOTE_FILTERS.map((filter) => (
          <ShippingNoteTab
            key={filter.id}
            type="button"
            $active={activeFilter === filter.id}
            onClick={() => setActiveFilter(filter.id)}
            aria-pressed={activeFilter === filter.id}
          >
            {filter.label}
          </ShippingNoteTab>
        ))}
      </ShippingNoteTabs>

      {!hasValidVendorId ? (
        <EmptyState>
          <StateTitle>Unable to load shipping notes</StateTitle>
          <ErrorMessage>
            Please select a valid outlet and try again.
          </ErrorMessage>
        </EmptyState>
      ) : null}

      {hasValidVendorId && isLoading ? <ShippingNoteListSkeleton /> : null}

      {hasValidVendorId && getAdvanceShippingNotesStatus === STATUS_FAIL ? (
        <EmptyState>
          <StateTitle>Unable to load shipping notes</StateTitle>
          <ErrorMessage>{advanceShippingNotesError}</ErrorMessage>
          <RetryButton type="button" onClick={fetchShippingNotes}>
            Try again
          </RetryButton>
        </EmptyState>
      ) : null}

      {getAdvanceShippingNotesStatus === STATUS_DONE &&
      advanceShippingNotes.length === 0 ? (
        searchQuery ? (
          <EmptyState>
            <StateTitle>No matching shipping notes</StateTitle>
            <span>Try another ASN ID.</span>
          </EmptyState>
        ) : (
          <AllCaughtUpState>
            <AllCaughtUpIcon>
              <img src="/logos/all_caught_up.svg" alt="" aria-hidden="true" />
            </AllCaughtUpIcon>
            <AllCaughtUpTitle>All caught up!</AllCaughtUpTitle>
            <span>
              {`No ${
                SHIPPING_NOTE_FILTERS.find(
                  (filter) => filter.id === activeFilter
                )?.label.toLowerCase() || "matching"
              } shipping notes are available.`}
            </span>
          </AllCaughtUpState>
        )
      ) : null}

      {visibleShippingNotes.length > 0 ? (
        <ShippingNoteCards>
          {visibleShippingNotes.map((shippingNote) => (
            <ShippingNoteListCard
              key={`${shippingNote.status}-${shippingNote.asn_id}`}
              shippingNote={shippingNote}
              onOpen={() =>
                navigate(
                  `/plan-shipment/shipping-notes/${shippingNote.asn_id}`,
                  {
                    state: {
                      asnNumber: formatShippingNoteNumber(shippingNote),
                    },
                  }
                )
              }
            />
          ))}
          {advanceShippingNotesHasMore ? (
            <div ref={sentinelRef} style={{ height: 1 }} />
          ) : null}
        </ShippingNoteCards>
      ) : null}
    </>
  );
};

export default ShippingNotesList;
